import styles from "./OnboardingStyles.module.css";
import React, { useEffect, useState } from "react";
import PrimaryButton from "../Button/PrimaryButton/PrimaryButton";
import { StepFormProps } from "../../types/onboarding.types";
import { useOrderContext } from "../../context/OrderContext";
import DatePicker from "../DatePicker/DatePicker";
import AddressInput from "../Input/AddressInput/AddressInput";
import { useTranslation } from "react-i18next";
import { useUserContext } from "../../context/UserContext";
import { useCartContext } from "../../context/CartContext";
import { normalizeDate, filterDatesOnOrAfterEffectiveTomorrow } from "../../utils/calendar";
import { useDeliveryAddresses } from "../../hooks/useDeliveryAddresses";
import { useUpdateCartHandler } from "../../hooks/useUpdateCartHandler";

const Step4Form = ({ currentStep, setStep, formData, setFormData }: StepFormProps) => {
    const { t } = useTranslation();
    const { orderData, handleOrder } = useOrderContext();
    const { userData } = useUserContext();
    const { cartLoaded, cartData } = useCartContext();

    const [address, setAddress] = useState(orderData.address);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);

    // Use consolidated hooks
    const subscriberId = cartData?.subscriber_id ?? orderData.recipient;
    const adresses = useDeliveryAddresses(subscriberId, userData);
    const { handleUpdateCart } = useUpdateCartHandler();

    // Initial selected dates from cartData.selected_dates (converted to ISO)
    useEffect(() => {
        const cartSelected = cartData?.selected_dates ?? [];
        if (Array.isArray(cartSelected) && cartSelected.length > 0) {
            setSelectedDates(
                filterDatesOnOrAfterEffectiveTomorrow(cartSelected.map(normalizeDate))
            );
            return;
        }
        // fallback (if cart not ready yet)
        setSelectedDates(filterDatesOnOrAfterEffectiveTomorrow(orderData.date ?? []));
    }, [cartData?.selected_dates]);

    // Drop dates that are no longer valid after cutoff
    useEffect(() => {
        if (selectedDates.length === 0) return;
        const filtered = filterDatesOnOrAfterEffectiveTomorrow(selectedDates);
        if (filtered.length !== selectedDates.length) {
            setSelectedDates(filtered);
        }
    }, [selectedDates]);

    const handleAction = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        setFormData((prev: typeof formData) => ({ ...prev, place: address, dates: selectedDates }));

        if (cartLoaded && cartData && selectedDates.length > 0) {
            handleUpdateCart(selectedDates, address);
        }

        if (address !== "") {
            handleOrder("address", address);
        }

        setStep(currentStep + 1);
    };

    const handleSkip = () => {
        setAddress("");
        setSelectedDates([]);
        setStep(currentStep + 1);
    };

    return (
        <>
        <div className={styles.OnboardingContent}>
            <div className={styles.Container}>
            <div className={styles.SecondaryTitle}>
                <p>{t("onboarding.steps.4.title")}</p>
            </div>

            <div className={styles.ActionButtons4}>
                <AddressInput
                onChange={setAddress}
                value={address !== "" ? address : orderData.address}
                addresses={adresses}
                className={styles.Address}
                wrapperClassName={styles.AddressWrap}
                />

                <DatePicker
                onChange={setSelectedDates}
                selectedDates={selectedDates}
                className={styles.Date}
                wrapperClassName={styles.DateWrap}
                />
            </div>

            <div className={styles.ContinueButton}>
                <div className={styles.ContinueChoice} onClick={() => handleSkip()}>
                <p></p>
                </div>
                <PrimaryButton onClick={(e) => handleAction(e)} text={t("buttons.proceed")} />
            </div>
            </div>
        </div>
        </>
    );
};

export default Step4Form;
